import { useState } from 'react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { GameButton } from '@/components/ui/GameButton'
import { Modal } from '@/components/ui/Modal'
import { useGameAudio } from '@/contexts/GameAudioProvider'
import { enterFullscreen, exitFullscreen, fullscreenSupported } from '@/game/fullscreen'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { useTutorial } from '@/contexts/TutorialProvider'
import { DEFAULT_SAVE, type GraphicsSettings } from '@/types/save'

function Slider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--text-dim)]">{label}</span>
        <span className="font-display font-bold tabular-nums">{Math.round(value * 100)}</span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--ember)]"
      />
    </label>
  )
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between w-full py-2"
    >
      <span className="text-[var(--text-dim)]">{label}</span>
      <span
        className="relative w-12 h-7 rounded-full transition-colors"
        style={{ background: checked ? 'var(--ember)' : 'var(--surface-sunken)' }}
      >
        <span
          className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all"
          style={{ left: checked ? '26px' : '4px' }}
        />
      </span>
    </button>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/40 p-4 flex flex-col gap-3">
      <h2 className="font-display font-bold text-[var(--text-dim)]">{title}</h2>
      {children}
    </div>
  )
}

export function SettingsScreen() {
  const audio = useGameAudio()
  const { go } = useNavigation()
  const { save, update } = useProgress()
  const { openTutorial } = useTutorial()
  const [mix, setMix] = useState(audio.mixState)
  const [confirmReset, setConfirmReset] = useState(false)

  const setSetting = <K extends keyof GraphicsSettings>(key: K, value: GraphicsSettings[K]) =>
    update((s) => ({ ...s, meta: { ...s.meta, settings: { ...s.meta.settings, [key]: value } } }))

  return (
    <ScreenShell title="Settings">
      <div className="flex flex-col gap-4">
        <Section title="Audio">
          <Slider label="Master" value={mix.master} onChange={(v) => { audio.setMaster(v); setMix({ ...mix, master: v }) }} />
          <Slider label="Music" value={mix.music} onChange={(v) => { audio.setMusic(v); setMix({ ...mix, music: v }) }} />
          <Slider label="Sound Effects" value={mix.sfx} onChange={(v) => { audio.setSfx(v); setMix({ ...mix, sfx: v }) }} />
          <Toggle label="Muted" checked={mix.muted} onChange={(v) => { audio.setMuted(v); setMix({ ...mix, muted: v }) }} />
        </Section>

        <Section title="Graphics">
          <div className="flex gap-2">
            {(['high', 'medium', 'low'] as const).map((tier) => (
              <button
                key={tier}
                onClick={() => setSetting('graphics', tier)}
                className={['flex-1 rounded-[var(--r-sm)] py-2 text-sm font-semibold capitalize transition', save.meta.settings.graphics === tier ? 'bg-[var(--ember)] text-[#231404]' : 'bg-[var(--surface-raised)] text-[var(--text-dim)]'].join(' ')}
              >
                {tier}
              </button>
            ))}
          </div>
          <Toggle label="Reduced Motion" checked={save.meta.settings.reducedMotion} onChange={(v) => setSetting('reducedMotion', v)} />
          <Toggle label="Colorblind-Safe Palette" checked={save.meta.settings.colorblind} onChange={(v) => setSetting('colorblind', v)} />
          {fullscreenSupported() && (
            <Toggle
              label="Fullscreen on Play"
              checked={save.meta.settings.autoFullscreen}
              onChange={(v) => {
                setSetting('autoFullscreen', v)
                if (v) void enterFullscreen()
                else void exitFullscreen()
              }}
            />
          )}
        </Section>

        <Section title="Feel">
          <Toggle label="Haptics" checked={save.meta.settings.haptics} onChange={(v) => setSetting('haptics', v)} />
        </Section>

        <Section title="About">
          <GameButton variant="secondary" icon="play" fullWidth onClick={openTutorial}>
            Replay Tutorial
          </GameButton>
          <div className="flex gap-2">
            <GameButton variant="secondary" icon="book" fullWidth onClick={() => go('help')}>
              How to Play
            </GameButton>
            <GameButton variant="ghost" icon="user" fullWidth onClick={() => go('credits')}>
              Credits
            </GameButton>
          </div>
        </Section>

        <Section title="Data">
          <GameButton variant="danger" icon="restart" fullWidth onClick={() => setConfirmReset(true)}>
            Reset Progress
          </GameButton>
        </Section>
      </div>

      <Modal open={confirmReset} onClose={() => setConfirmReset(false)} title="Reset progress?">
        <p className="text-[var(--text-dim)] mb-5">This clears levels, stars, currency, and cosmetics on this device. This cannot be undone.</p>
        <div className="flex gap-2">
          <GameButton variant="ghost" fullWidth onClick={() => setConfirmReset(false)}>
            Cancel
          </GameButton>
          <GameButton
            variant="danger"
            fullWidth
            onClick={() => {
              update(() => ({ ...DEFAULT_SAVE, updatedAt: Date.now() }))
              setConfirmReset(false)
            }}
          >
            Reset
          </GameButton>
        </div>
      </Modal>
    </ScreenShell>
  )
}
